import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import Input from "../input/Input";
import SocialLoginButtons from "../social-login/SocialLoginButtons";
import { useAppDispatch } from "../../app/hooks";
import { loginUserAsync } from "../../features/authentication/authenticationApi";
import { UserLoginRequest } from "../../features/authentication/UserLoginRequest";
import { useSocialLogin } from "../../features/authorization/useSocialLogin";
import { resolvePathAfterSignIn } from "../../features/contexts/utils/postSignIn";
import { validateEmail } from "./authValidation";

interface SignInFormProps {
  onRegister: () => void;
  onResetPassword: () => void;
  onPasswordless: () => void;
}

const SignInForm = ({ onRegister, onResetPassword, onPasswordless }: SignInFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { socialLoading, socialError, loginWithProvider } = useSocialLogin();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const busy = loading || socialLoading;
  const displayError = error ?? socialError;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setEmailError(null);
    setPasswordError(null);

    if (email.trim() === "") {
      setEmailError(t("auth.errors.emailRequired"));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t("auth.errors.invalidEmailFormat"));
      return;
    }

    if (password.trim() === "") {
      setPasswordError(t("auth.errors.passwordRequired"));
      return;
    }

    setLoading(true);

    try {
      const userLoginRequest: UserLoginRequest = { email, password };
      const resultAction = await dispatch(loginUserAsync(userLoginRequest));

      if (loginUserAsync.fulfilled.match(resultAction)) {
        // A pending invitation, else the context chooser when there is more than one context.
        navigate(await resolvePathAfterSignIn(dispatch));
      } else if (loginUserAsync.rejected.match(resultAction)) {
        const status = resultAction.payload?.status;
        switch (status) {
          case HttpStatusCode.BadRequest:
            setError(t("auth.errors.invalidRequest"));
            break;
          case HttpStatusCode.Unauthorized:
            setError(t("auth.errors.unauthorized"));
            break;
          case HttpStatusCode.Forbidden:
            setError(t("auth.errors.accountNotVerified"));
            break;
          case HttpStatusCode.InternalServerError:
            setError(t("auth.errors.serverError"));
            break;
          default:
            setError(t("auth.errors.unknownError"));
            break;
        }
      }
    } catch {
      setError(t("auth.errors.unexpectedError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <h2 className="text-grey-20 dark:text-white">{t("auth.signIn")}</h2>
      <p className="mt-2 text-grey-50 dark:text-grey-70">
        {t("auth.signInSubtitle")}
      </p>

      <div className="mt-6">
        <Input
          label={t("auth.email")}
          type="email"
          name="loginEmail"
          id="loginEmail"
          fullWidth
          margin="dense"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          disabled={busy}
          error={!!emailError}
          helperText={emailError}
          labelPosition="top-left"
        />
        <Input
          label={t("auth.password")}
          type="password"
          name="loginPassword"
          id="loginPassword"
          fullWidth
          margin="dense"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (passwordError) setPasswordError(null);
          }}
          disabled={busy}
          error={!!passwordError}
          helperText={passwordError}
          showPasswordToggle={true}
          labelPosition="top-left"
        />
      </div>

      {displayError && (
        <p className="mb-2 text-sm p-2 rounded-lg text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">
          {displayError}
        </p>
      )}

      <button
        type="submit"
        className="btn btn-primary w-full h-[46px] mt-2 flex items-center justify-center text-[16px] no-underline"
        disabled={busy}
      >
        {t("auth.signIn")}
      </button>

      <div className="mt-4 flex items-center justify-center gap-5 text-center">
        <button
          type="button"
          className="text-sm cursor-pointer hover:underline text-primary-40 dark:text-primary-90 bg-transparent border-none"
          onClick={onResetPassword}
        >
          {t("auth.forgotPassword")}
        </button>
        <button
          type="button"
          className="text-sm cursor-pointer hover:underline text-primary-40 dark:text-primary-90 bg-transparent border-none"
          onClick={onPasswordless}
        >
          {t("auth.passwordless.sendEmail", "Email me a sign-in link")}
        </button>
      </div>

      <SocialLoginButtons onProvider={loginWithProvider} loading={busy} />

      {/* New visitors land here too (a bookmark, a shared link), and a text link at the
          bottom was easy to miss on a phone — so the trial gets its own bordered block
          with a real button and the promise spelled out. */}
      <div className="mt-6 rounded-2xl border border-grey-70 p-4 text-center dark:border-grey-50 [@media(max-height:760px)]:mt-3 [@media(max-height:760px)]:p-3">
        <p className="text-sm font-bold text-grey-20 dark:text-white">{t("auth.newHere")}</p>
        <p className="mt-1 text-xs text-grey-50 dark:text-grey-60">{t("auth.newHereSubtitle")}</p>
        <button
          type="button"
          onClick={onRegister}
          disabled={busy}
          className="btn btn-secondary mt-3 h-[46px] w-full text-[16px]"
        >
          {t("auth.createFreeAccount")}
        </button>
      </div>
    </form>
  );
};

export default SignInForm;
